import { pushEvent } from "./gtm";

type NoroshiReserveStepDetail = {
  step?: unknown;
  sessionId?: unknown;
  salonboardReserveId?: unknown;
};

const MICRO_CONVERSION_EVENTS = {
  menu: "noroshi_reservation_started",
  staff: "noroshi_reservation_menu_selected",
  datetime: "noroshi_reservation_staff_selected",
  confirm: "noroshi_reservation_datetime_selected",
  otp_sent: "noroshi_reservation_auth_started",
  otp_verified: "noroshi_reservation_confirm_viewed",
} as const;

const sentSteps = new Set<string>();
let initialized = false;

// Thin measurement bridge for the public Noroshi Web Component.
// Reservation state stays in the component; Pages only forwards privacy-safe
// funnel signals to GTM's dataLayer. Compatibility consumers can forward each
// push immediately, so session IDs must stay in the in-memory dedupe keys only.
export function initializeNoroshiReserveBridge() {
  if (initialized) return;
  initialized = true;

  document.addEventListener("noroshi-reserve-step", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.tagName !== "NOROSHI-RESERVE") return;

    const detail = (event as CustomEvent).detail as NoroshiReserveStepDetail | null;
    if (!detail || typeof detail.sessionId !== "string" || !detail.sessionId) return;

    const salonId = target.getAttribute("salon-id");
    if (!salonId) return;

    const step = typeof detail.step === "string" ? detail.step : "";
    const eventName = Object.hasOwn(MICRO_CONVERSION_EVENTS, step)
      ? MICRO_CONVERSION_EVENTS[step as keyof typeof MICRO_CONVERSION_EVENTS]
      : undefined;

    if (eventName) {
      const key = `${detail.sessionId}:${step}`;
      if (sentSteps.has(key)) return;
      sentSteps.add(key);
      pushEvent(eventName, {
        salon_id: salonId,
      });
      return;
    }

    if (step !== "done") return;

    const key = `${detail.sessionId}:done`;
    if (sentSteps.has(key)) return;
    sentSteps.add(key);

    const reservationId =
      typeof detail.salonboardReserveId === "string" &&
      detail.salonboardReserveId &&
      detail.salonboardReserveId !== detail.sessionId
        ? detail.salonboardReserveId
        : undefined;

    pushEvent("noroshi_reservation_complete", {
      salon_id: salonId,
      ...(reservationId ? { transaction_id: reservationId } : {}),
    });
  });
}

export function pushNoroshiReserveCtaClick(params: {
  salonId: string;
  menuId?: string | null;
  staffId?: string | null;
  mode?: string | null;
  resume: boolean;
}) {
  pushEvent("noroshi_reservation_cta_click", {
    salon_id: params.salonId,
    ...(params.menuId ? { menu_id: params.menuId } : {}),
    ...(params.staffId ? { staff_id: params.staffId } : {}),
    ...(params.mode ? { mode: params.mode } : {}),
    resume: params.resume,
  });
}
